import { useEffect, useState } from 'react'
import api from '../../services/api.js'
import BankDocumentReader from './BankDocumentReader.jsx'
import { useTranslation } from 'react-i18next'

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'AED', 'SAR']

const BLANK = {
  account_holder_name: '',
  bank_name: '',
  bank_address: '',
  account_number: '',
  iban: '',
  swift_code: '',
  currency: 'USD',
  country: '',
}

/** Only the fields this form owns, with nulls turned into empty strings so the inputs stay
 *  controlled. */
const seed = (d) => Object.fromEntries(
  Object.keys(BLANK).map(k => [k, d?.[k] ?? BLANK[k]])
)

/**
 * `initial` lets a caller that already holds the details pass them straight in; otherwise the
 * form fetches them. Either way it opens on what is stored rather than blank — banking is one
 * row per person, so "edit" always means editing that row, and starting empty invited someone
 * to retype an account number they only meant to correct a digit of.
 */
export default function BankingDetailsForm({ contractId, initial, onSaved, onCancel }) {
  const { t } = useTranslation('consultants')
  const { t: tc } = useTranslation('common')
  const [form, setForm] = useState(initial ? seed(initial) : BLANK)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (initial) return
    let alive = true
    api.get('/users/me/banking')
      .then(r => { if (alive && r.data?.has_banking) setForm(seed(r.data)) })
      .catch(() => { /* nothing stored, or not readable — the blank form is the right fallback */ })
    return () => { alive = false }
  }, [initial])

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const handleSubmit = async e => {
    e.preventDefault()
    const required = ['account_holder_name', 'bank_name', 'account_number', 'iban', 'swift_code', 'country']
    for (const k of required) {
      if (!form[k].trim()) {
        setError(`${k.replace(/_/g, ' ')} is required`)
        return
      }
    }

    setSubmitting(true)
    setError(null)
    try {
      await api.put(`/contracts/${contractId}/banking-details`, form)
      onSaved?.()
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save banking details')
    } finally {
      setSubmitting(false)
    }
  }

  // Inline input class — fields are rendered inline (not via a nested component)
  // to avoid React remounting them on every keystroke (which caused focus loss).
  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
  const labelCls = "block text-xs font-medium text-gray-600 mb-1"

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">{t('banking_payment_details')}</h3>
        <p className="text-xs text-gray-500">
          Your banking information is encrypted at rest and only used for payment processing.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
      )}

      <BankDocumentReader onApply={read => setForm(f => ({ ...f, ...read }))} />

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className={labelCls}>{t('account_holder')} <span className="text-red-500">*</span></label>
          <input type="text" data-private value={form.account_holder_name} onChange={e => set('account_holder_name', e.target.value)} placeholder={t('ph_full_legal')} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t('bank_name')} <span className="text-red-500">*</span></label>
            <input type="text" value={form.bank_name} onChange={e => set('bank_name', e.target.value)} placeholder="e.g. HSBC" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t('bank_address')}</label>
            <input type="text" data-private value={form.bank_address} onChange={e => set('bank_address', e.target.value)} placeholder={t('ph_city_country')} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>{t('account_number')} <span className="text-red-500">*</span></label>
          <input type="text" data-private value={form.account_number} onChange={e => set('account_number', e.target.value)} placeholder={t('ph_account_number')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>IBAN <span className="text-red-500">*</span></label>
          <input type="text" data-private value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="e.g. GB29NWBK60161331926819" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t('swift_bic_code')} <span className="text-red-500">*</span></label>
            <input type="text" data-private value={form.swift_code} onChange={e => set('swift_code', e.target.value)} placeholder="e.g. HBUKGB4B" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{tc('labels.currency')} <span className="text-red-500">*</span></label>
            <select
              value={form.currency}
              onChange={e => set('currency', e.target.value)}
              className={inputCls}
            >
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>{t('country')} <span className="text-red-500">*</span></label>
          <input type="text" value={form.country} onChange={e => set('country', e.target.value)} placeholder={t('ph_bank_country')} className={inputCls} />
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save Banking Details'}
        </button>
      </div>
    </form>
  )
}
